import React from 'react';
import { ShieldCheck, SearchCheck, UserCheck, FileDown, GraduationCap, AlertTriangle } from 'lucide-react';
import { PageHeader } from '../../components/ui';
import '../../styles/theme.css';

const CARDS = [
  {
    icon: <SearchCheck size={18} />, title: 'Every value is verified against your document',
    body: 'DocShawk doesn’t just take the AI’s word for it. For every term it pulls out, it checks — '
      + 'mechanically, not with more AI — that the value actually appears in your document’s text. '
      + 'A value that checks out is marked “Verified in document.” Anything it can’t verify is flagged '
      + 'for your review instead of being silently trusted.',
  },
  {
    icon: <UserCheck size={18} />, title: 'You confirm before anything is final',
    body: 'Nothing is exported until a person reviews it. DocShawk surfaces the handful of values that '
      + 'need attention first; you confirm them or correct them by pointing at the passage in the '
      + 'document. Required checks must pass (or be explicitly overridden) before a document can finish.',
  },
  {
    icon: <FileDown size={18} />, title: 'Every result carries its citation',
    body: 'The exported file lists each term with its value and the exact page and passage it came from — '
      + 'ready to drop into an audit trail or diligence file. If a term isn’t in the agreement, it says so, '
      + 'rather than guessing.',
  },
  {
    icon: <ShieldCheck size={18} />, title: 'Confidential documents never leave your systems',
    body: 'A document marked confidential is analyzed only within your firm’s approved systems and local '
      + 'models — it is never sent to public AI services. This is enforced structurally, not as a setting '
      + 'you can forget to turn on.',
  },
  {
    icon: <GraduationCap size={18} />, title: 'It learns from your corrections',
    body: 'When your team confirms or corrects values, those become the ground truth DocShawk trains on. '
      + 'Over time the open-source model gets better at your documents — and a new model is only adopted '
      + 'if it measurably beats the current one on documents it hasn’t seen.',
  },
];

const HelpTrust: React.FC = () => (
  <div>
    <PageHeader title="Help & Trust"
      subtitle="How DocShawk reads your documents — and why you can rely on the result." />

    <div className="mx-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))' }}>
      {CARDS.map((c) => (
        <div key={c.title} className="mx-card mx-card-pad">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--mx-accent)', marginBottom: 8 }}>
            {c.icon}<span style={{ fontWeight: 600, color: 'var(--mx-text)', fontSize: 15 }}>{c.title}</span>
          </div>
          <p className="mx-muted" style={{ fontSize: 13.5, lineHeight: 1.6, margin: 0 }}>{c.body}</p>
        </div>
      ))}
    </div>

    <div className="mx-card mx-card-pad" style={{ marginTop: 16, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <AlertTriangle size={18} style={{ color: 'var(--mx-warning)', flex: 'none', marginTop: 1 }} />
      <div>
        <div style={{ fontWeight: 600, marginBottom: 3 }}>DocShawk assists — it doesn’t replace review</div>
        <div className="mx-muted" style={{ fontSize: 13, lineHeight: 1.6 }}>
          Treat extracted values as a fast, cited first pass. The verification and review steps exist so a
          person always has the final say on what’s relied upon.
        </div>
      </div>
    </div>
  </div>
);

export default HelpTrust;
